import { Activity, Database, Route } from "lucide-react";
import type { DashboardData } from "@/lib/types";
import { collectorHealth, extractionPathLabel } from "@/lib/system-health";
import { Badge, Panel, PanelHeader } from "./ui";

function formatTimestamp(value?: string | null) {
  if (!value) return "Not reported";
  const date = new Date(value);
  return Number.isNaN(date.valueOf())
    ? value
    : `${new Intl.DateTimeFormat("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
        timeZone: "UTC"
      }).format(date)} UTC`;
}

export function SystemStatusPanel({ data }: { data: DashboardData["collector"] }) {
  const run = data.run;
  const success = run?.state === "success" || run?.state === "succeeded";
  const runTone = collectorHealth(run).tone;

  return (
    <section id="system-status" aria-labelledby="system-status-heading" className="space-y-4">
      <div>
        <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[var(--hc-muted)]">Operations</p>
        <h2 id="system-status-heading" className="mt-1 text-xl font-semibold tracking-[-0.025em] text-[var(--hc-text)]">System Status</h2>
        <p className="mt-1 text-sm text-[var(--hc-muted)]">Collector runtime health. Sensitive sessions and restricted series identities are never shown.</p>
      </div>
      <Panel>
        <PanelHeader title="Collector run" meta={run?.mode ?? "not reported"} />
        <div className="grid gap-3 sm:grid-cols-3">
          <StatusFact icon={Activity} label="Latest run state" value={run?.state?.toUpperCase() ?? "NOT REPORTED"}>
            <Badge tone={runTone}>{run?.state?.toUpperCase() ?? "NO RUN"}</Badge>
          </StatusFact>
          <StatusFact
            icon={Database}
            label={success ? "Last successful run" : "Latest run finished"}
            value={formatTimestamp(run?.finished_at)}
          />
          <StatusFact icon={Route} label="Extraction path" value={extractionPathLabel(run?.extraction)} />
        </div>
      </Panel>
    </section>
  );
}

function StatusFact({ icon: Icon, label, value, children }: { icon: typeof Activity; label: string; value: string; children?: React.ReactNode }) {
  return (
    <div className="rounded-md border border-[var(--hc-border)] bg-[var(--hc-surface-2)] p-3">
      <div className="flex items-center gap-2 text-[var(--hc-muted)]">
        <Icon size={15} aria-hidden="true" />
        <p className="text-[11px] font-semibold uppercase tracking-[0.08em]">{label}</p>
      </div>
      <div className="mt-2 text-sm font-medium text-[var(--hc-text)]">{children ?? value}</div>
    </div>
  );
}
